import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import querystring from 'querystring';

import {
    Button,
    Modal,
    ModalHeader,
    ModalTitle,
} from 'reaxl';
import { sendClick, sendImpressions } from 'reaxl-analytics';

import { dealerRatingClick, dealerRatingImpressions } from '@/components/RatingsReviews/analytics/dealerRating';
import IframeKBBDealerRatings from '@/components/RatingsReviews/IframeKBBDealerRatings';
import KBBInlineRatings from '@/components/RatingsReviews/KBBInlineRatings';

const DEPRECATED_TYPES = ['stacked', 'large'];

/**
 *  Displays KBB dealer ratings
 *
 */
function KBBDealerRatings({
    enableImpression = false,
    enableClick = true,
    className,
    owner,
    showCount = false,
    widgetType = 'inline',
    defaultShowModal = false,
    onToggleModal,
    ...rest
}) {
    const pageName = useSelector((state) => state?.currentPageName || '');

    // TODO: removing VDP logic would be breaking change now, but should u/d FYC to
    // only pass enableImpression from VDP, then simplify this.
    // DEPRECATE most of impression logic
    if (pageName !== 'vdp' || DEPRECATED_TYPES.includes(widgetType)) {
        enableImpression = false;
    }

    const [showModal, setShowModal] = useState(defaultShowModal);

    const {
        id,
        rating: {
            count,
            value,
            apiKey,
            apiUrl,
        } = {},
    } = owner ?? {};

    useEffect(() => {
        if (enableImpression && value) {
            sendImpressions({ name: dealerRatingImpressions, data: { id } });
        }
    }, [value, id, pageName, enableImpression, widgetType]);

    // Deprecate the iframe widgets
    if (DEPRECATED_TYPES.includes(widgetType)) {
        // eslint-disable-next-line no-console
        console.log(`${widgetType} is deprecated. Please use the IframeKBBDealerRatings component`);
        return (
            <IframeKBBDealerRatings
                owner={owner}
                widgetType={widgetType}
                className={className}
                {...rest}
            />
        );
    }

    const params = {
        apiKey,
        svocId: id,
        nocount: showCount,
    };

    const urlParams = querystring.stringify(params);

    const toggleModal = (event) => {
        if (typeof onToggleModal === 'function') {
            try {
                onToggleModal(event);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }

        if (!showModal && enableClick) {
            sendClick(dealerRatingClick, event, {
                ratingSource: 'kbb',
                value,
                count,
                id,
            });
        }
        setShowModal(!showModal);
    };

    const modalSrc = `${apiUrl}/v2/widget/ratings/overlay?` + urlParams;

    return (
        <React.Fragment key="kbbDealerRatings">
            {widgetType === 'link' && (
                <Button
                    className={className}
                    onClick={toggleModal}
                    bsStyle="text"
                >
                    Learn More
                </Button>
            )}
            {widgetType === 'inline' && !!value && (
                <KBBInlineRatings
                    rating={Number(value)}
                    count={showCount ? count : undefined}
                    handleCountClick={toggleModal}
                    className={className}
                    label="Dealer"
                />
            )}
            {showModal && (
                <Modal
                    show={showModal}
                    onHide={toggleModal}
                >
                    {/* This is a non-standard implementation. Do not copy */}
                    <ModalHeader closeButton>
                        <ModalTitle className="sr-only">
                            KBB.com Dealer Rating
                        </ModalTitle>
                        <iframe
                            id="kdr-popup-content"
                            data-cmp="kbbDealerRatings"
                            width="100%"
                            height={500}
                            title="KBB.com Dealer Rating Details"
                            className="border-0"
                            src={modalSrc}
                            {...rest}
                            key="kdr-popup-content"
                        />
                    </ModalHeader>
                </Modal>
            )}
        </React.Fragment>
    );
}

KBBDealerRatings.propTypes = {
    /**
    *   Triggers tagging impression when true
    */
    enableImpression: PropTypes.bool,
    /**
    *   Triggers tagging click when true
    */
    enableClick: PropTypes.bool,
    className: PropTypes.string,
    /**
    *   Callback for modal show/hide
    */
    onToggleModal: PropTypes.func,
    /**
    *   Standard Owner Object. Requires `id` and `rating` object
    */
    owner: PropTypes.object.isRequired,
    /**
    *   Type of widget to display
    */
    widgetType: PropTypes.oneOf([
        'inline',
        'link',
    ]),
    /**
    *   Displays number of ratings when true (`inline` only)
    */
    showCount: PropTypes.bool,
    /**
    *   Displays the modal on load when true
    */
    defaultShowModal: PropTypes.bool,
};

export default KBBDealerRatings;
